/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React, { useEffect }  from 'react';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import NativeSelect from '@material-ui/core/NativeSelect';
import FormHelperText from '@material-ui/core/FormHelperText';
import InputAdornment from '@material-ui/core/InputAdornment';
import IconButton from '@material-ui/core/IconButton';

import {
    object, bool, string, func, array,
} from 'prop-types';
import { withStyles } from '@material-ui/core/styles';

import Info from '@material-ui/icons/Info';
import Cancel from '@material-ui/icons/Cancel';

const styles = (theme) => {
    const { palette } = theme;
    return ({
        rootFormControl: {
            border: `solid 1px ${palette.grey8}`,
            width: '100%',
            '& select:-webkit-autofill, & select:-webkit-autofill:hover, & select:-webkit-autofill:focus': {
                '-webkit-box-shadow': '0 0 0 30px white inset !important',
            },
        },
        rootErrorIcon: {
            color: palette.error.light,
        },
        rootInputLabel: {
            fontSize: '14px',
            fontWeight: '500',
            paddingLeft: '10px',
            paddingTop: '0',
            lineHeight: '14px',
            textTransform: 'uppercase',
            top: '0',
        },
        asteriskInputLabel: {
            margin: '0',
            fontSize: '16px',
        },
        redAsterisk: {
            color: 'red',
        },
        rootFormHelperText: {
            margin: '0px',
            padding: '3px 10px',
        },
        formHelperText: {
            fontSize: '12px',
            color: palette.white,
        },
        errorColor: {
            backgroundColor: 'red',
        },
        infoColor: {
            backgroundColor: palette.colorNeutral90,
        },
        infoArrow: {
            margin: '0px 0px',
            width: '0',
            height: '0',
            borderLeft: '5px solid transparent',
            borderRight: '5px solid transparent',
            borderBottom: `5px solid ${palette.colorNeutral60}`,
            position: 'absolute',
            right: '18px',
            top: '47px',
        },
        rootNativeSelect: {
            padding: '2px 10px',
        },
        selectNativeSelect: {
            padding: '7px 38px 3px 7px',
            color: palette.colorNeutral60,
            fontWeight: '300',
            '&:active, &:hover, &:focus': {
                backgroundColor: theme.palette.white,
            },
        },
        positionEndInputAdornment: {
            marginLeft: '0px',
        },
        noBorderBottom: {
            borderBottom: 'none !important',
        },
        '@keyframes stroke': {
            '100%': {
                strokeDashoffset: '0',
            },
        },
        '@keyframes scale': {
            '0%, 100%': {
                transform: 'none',
            },
            '50%': {
                transform: 'scale3d(1.1, 1.1, 1)',
            },
        },
        '@keyframes fill': {
            '100%': {
                boxShadow: `inset 0px 0px 0px 40px ${theme.palette.successButton}`,
            },
        },
        checkmarkCircle: {
            strokeDasharray: '166',
            strokeDashoffset: '166',
            strokeWidth: '2',
            strokeMiterlimit: '10',
            stroke: theme.palette.successButton,
            fill: 'none',
            animation: '$stroke 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards',
        },
        checkmark: {
            width: '16px',
            height: '16px',
            borderRadius: '50%',
            display: 'inline-block',
            strokeWidth: 4,
            stroke: theme.palette.white,
            strokeMiterlimit: '10',
            margin: '-2px 5px 0',
            boxShadow: `inset 0px 0px 0px ${theme.palette.successButton}`,
            animation: '$fill .4s ease-in-out .4s forwards, $scale .3s ease-in-out .9s both',
        },
        checkmarkCheck: {
            transformOrigin: '50% 50%',
            strokeDasharray: '38',
            strokeDashoffset: '5',
            animation: '$stroke 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.8s forwards',
        },
    });
};

const CustomInputField = (props) => {
    const {
        fieldId,
        name,
        label,
        classes,
        showInfo,
        isRequired,
        autoComplete,
        handleChange,
        handleBlur,
        handleOnFocus,
        handleOnKeyUp,
        val,
        flagError,
        errorMessage,
        infoHelpText,
        disabled,
        dropdown,
        toggleDisplayFieldInfo,
        showIntputBox,
        track,
        redAsterisk,
    } = props;
    const endAdornment = (infoHelpText)
        ? (
            <InputAdornment position="end">
                <IconButton
                    onClick={toggleDisplayFieldInfo}
                >
                    {infoHelpText && (showInfo ? <Cancel /> : <Info />)}
                </IconButton>
            </InputAdornment>
        ) : null;
    const options = dropdown.map((item) => {
        const keyItem = `${name}_${item.key}`;
        return <option key={keyItem} value={item.key}>{item.value}</option>;
    });

    useEffect(() => {
        if (flagError && errorMessage?.trim() !== '') {
            track({
                eventCategory: 'Checkout',
                eventAction: 'Checkout Step|<<pageType>>',
                eventLabel: `Field Error - ${errorMessage}`,
            });
        }
    }, [flagError]);

    return (
        <FormControl
            key={`formControl_${name}_${fieldId}`}
            classes={{
                root: classes.rootFormControl,
            }}
            className={((showIntputBox) ? classes.noBorderBottom : '')}
            error={flagError}
        >
            <InputLabel
                classes={{
                    root: classes.rootInputLabel,
                }}
                shrink
                htmlFor={name}
            >
                <span>
                    <span hidden={!val ? 'hidden' : ''}>{label}</span>
                    {isRequired && val && <span className={`${classes.asteriskInputLabel} ${redAsterisk ? classes.redAsterisk : ''}`}>*</span>}
                    {!flagError && isRequired && val && (
                        <svg
                            className={classes.checkmark}
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 52 52"
                        >
                            <circle className={classes.checkmarkCircle} cx="26" cy="26" r="25" fill="none" />
                            <path className={classes.checkmarkCheck} fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
                        </svg>
                    )}
                </span>
            </InputLabel>
            <NativeSelect
                key={`tf_input_${fieldId}`}
                classes={{
                    root: classes.rootNativeSelect,
                    select: classes.selectNativeSelect,
                }}
                disableUnderline
                inputProps={{
                    name,
                    id: fieldId,
                    autoComplete,
                    disabled,
                    'data-testid': `select-${fieldId}`, // This is for unit test
                }}
                onChange={handleChange}
                onBlur={handleBlur}
                onFocus={handleOnFocus}
                onKeyUp={handleOnKeyUp}
                value={val}
                error={flagError}
                data-test={`checkout-text-field-div-wrapper-${name}`}
                endAdornment={endAdornment}
            >
                {options}
            </NativeSelect>
            {showInfo && (
                <FormHelperText
                    id={`helper-text-${fieldId}`}
                    inputlabelprops={{
                        shrink: true,
                    }}
                    classes={{
                        root: `${classes.rootFormHelperText} ${classes.infoColor}`,
                    }}
                >
                    <span className={classes.infoArrow} />
                    <span className={classes.formHelperText}>
                        {infoHelpText}
                    </span>
                </FormHelperText>
            )}
            {(flagError && !showIntputBox) && (
                <FormHelperText
                    id={`helper-text-${fieldId}`}
                    inputlabelprops={{
                        shrink: true,
                    }}
                    classes={{
                        root: `${classes.rootFormHelperText} ${classes.errorColor}`,
                    }}
                >
                    <span className={classes.formHelperText}>
                        {errorMessage}
                    </span>
                </FormHelperText>
            )}
        </FormControl>
    );
};

CustomInputField.defaultProps = {
    track: () => { },
    label: '',
    fieldId: '',
    classes: {},
    infoHelpText: '',
    showInfo: false,
    isRequired: false,
    name: '',
    val: '',
    errorMessage: null,
    flagError: null,
    disabled: false,
    autoComplete: 'off',
    handleOnFocus: () => { },
    handleOnKeyUp: () => { },
    handleChange: () => {},
    handleBlur: () => {},
    toggleDisplayFieldInfo: () => {},
    dropdown: [],
    showIntputBox: false,
    redAsterisk: false,
};

CustomInputField.propTypes = {
    label: string,
    fieldId: string,
    classes: object,
    infoHelpText: string,
    showInfo: bool,
    isRequired: bool,
    val: string,
    name: string,
    handleOnFocus: func,
    handleOnKeyUp: func,
    errorMessage: string,
    flagError: bool,
    disabled: bool,
    autoComplete: string,
    handleChange: func,
    handleBlur: func,
    dropdown: array,
    toggleDisplayFieldInfo: func,
    showIntputBox: bool,
    track: func,
    redAsterisk: bool,
};

export default withStyles(styles)(CustomInputField);
